import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";

/**
 * Hook — fetches the current user's plan features (modules + features unlocked)
 * and exposes helpers to check gating client-side.
 *
 * Backend endpoint: GET /api/subscription/features/my
 *
 * Usage:
 *   const { planId, planName, isTrial, hasModule, hasFeature, loading } = usePlanFeatures();
 *   if (!hasModule("finance")) return <PaywallCard module="finance" />;
 */
export function usePlanFeatures() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/subscription/features/my");
      setData(res);
    } catch {
      // Silent fallback: assume Starter (safest minimum).
      setData({
        plan_id: "plan_starter",
        plan_name: "Starter",
        is_trial: false,
        modules: ["dashboard", "menus", "customers", "website", "settings", "subscription", "help"],
        features: [],
        all_modules: [],
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const moduleSet = new Set(data?.modules || []);
  const featureSet = new Set(data?.features || []);

  return {
    planId: data?.plan_id || "plan_starter",
    planName: data?.plan_name || "Starter",
    isTrial: !!data?.is_trial,
    allModules: data?.all_modules || [],
    hasModule: (key) => moduleSet.has(key),
    hasFeature: (key) => featureSet.has(key),
    loading,
    refresh: load,
  };
}
